import * as Sentry from '@sentry/gatsby';

const REDACTED = '[REDACTED]';
const SENSITIVE_KEYS = new Set([
	'accessToken',
	'address',
	'annualIncome',
	'apiKey',
	'apikey',
	'api_key',
	'auth',
	'authorization',
	'bank',
	'bank_accounts',
	'bankAccount',
	'bankOwnership',
	'bic',
	'birth',
	'cookie',
	'credential',
	'customer_id',
	'DateOfBirth',
	'DateOfExpiry',
	'display_name',
	'dni',
	'document',
	'documents',
	'email',
	'firebase_id',
	'firstName',
	'firstname_validated',
	'fullName',
	'fundsOrigin',
	'fundsOriginFileType',
	'gender',
	'iban',
	'identification',
	'kyc',
	'kyc_data',
	'kyc_id',
	'kyc_personal',
	'lastName',
	'lastname_validated',
	'limitAmount',
	'mfa_phone_e164',
	'mobile',
	'name',
	'nameAdministrator',
	'nameBusiness',
	'nationality',
	'nie',
	'nif',
	'nifAdministrator',
	'nifBusiness',
	'pass',
	'passport',
	'password',
	'patrimonyAmount',
	'personalInformation',
	'pbc_economic_activity',
	'phone',
	'phoneNumber',
	'photo_url',
	'privateKey',
	'private_key',
	'providerId',
	'residenciaFiscalCustom',
	'residenciaFiscalFile',
	'result_callback_1',
	'result_callback_2',
	'secret',
	'session',
	'surname',
	'swift',
	'token',
	'url',
	'user_data_temp',
]);

const isSensitiveKey = (key: string): boolean => {
	const normalizedKey = key.toLowerCase();
	return Array.from(SENSITIVE_KEYS).some((sensitiveKey) => normalizedKey.includes(sensitiveKey.toLowerCase()));
};

const sanitizeSentryExtra = (value: any, depth = 0): any => {
	if (depth > 6) return '[MaxDepth]';
	if (value === null || value === undefined) return value;
	if (typeof value !== 'object') return value;

	if (Array.isArray(value)) {
		return value.map((item) => sanitizeSentryExtra(item, depth + 1));
	}

	return Object.entries(value).reduce<Record<string, any>>((acc, [key, nestedValue]) => {
		acc[key] = isSensitiveKey(key) ? REDACTED : sanitizeSentryExtra(nestedValue, depth + 1);
		return acc;
	}, {});
};

/**
 * Captura errores genéricos
 */
export const captureError = (error: Error | string, category: string = 'generic', extra?: Record<string, any>) => {
	Sentry.captureException(error, {
		tags: {
			category,
		},
		level: 'error',
		extra: sanitizeSentryExtra(extra),
	});
};

/**
 * Captura errores relacionados con blockchain y wallets
 */
export const captureBlockchainError = (error: Error, operation: string, extra?: Record<string, any>) => {
	Sentry.captureException(error, {
		tags: {
			category: 'blockchain',
			operation,
		},
		level: 'error',
		extra: sanitizeSentryExtra(extra),
	});
};

/**
 * Captura errores de API y GraphQL
 */
export const captureAPIError = (error: Error | string, endpoint: string, extra?: Record<string, any>) => {
	Sentry.captureException(error, {
		tags: {
			category: 'api',
			endpoint,
		},
		level: 'error',
		extra: sanitizeSentryExtra(extra),
	});
};

/**
 * Captura errores críticos de pagos y transacciones
 */
export const capturePaymentError = (error: Error, operation: string, extra?: Record<string, any>) => {
	Sentry.captureException(error, {
		tags: {
			category: 'payment',
			operation,
			severity: 'critical',
		},
		level: 'error',
		extra: sanitizeSentryExtra(extra),
	});
};

/**
 * Captura errores de autenticación
 */
export const captureAuthError = (error: Error, provider: string, extra?: Record<string, any>) => {
	Sentry.captureException(error, {
		tags: {
			category: 'auth',
			provider,
		},
		level: 'warning',
		extra: sanitizeSentryExtra(extra),
	});
};

/**
 * Agrega breadcrumb personalizado
 */
export const addBreadcrumb = (message: string, category: string, data?: Record<string, any>) => {
	Sentry.addBreadcrumb({
		message,
		category,
		level: 'info',
		data: sanitizeSentryExtra(data),
	});
};

/**
 * Establece contexto personalizado
 */
export const setContext = (key: string, context: Record<string, any>) => {
	Sentry.setContext(key, sanitizeSentryExtra(context));
};

/**
 * Establece usuario en Sentry
 */
export const setUser = (
	user: {
		id: string;
		email?: string;
		username?: string;
		[key: string]: any;
	} | null
) => {
	Sentry.setUser(user);
};

/**
 * Captura mensaje informativo (no error)
 */
export const captureMessage = (message: string, level: Sentry.SeverityLevel = 'info', extra?: Record<string, any>) => {
	Sentry.captureMessage(message, {
		level,
		extra: sanitizeSentryExtra(extra),
	});
};
